<script setup>
import { ref } from 'vue'
import { useToast } from 'vue-toastification'
import Signal from '@/api/Signal'

const props = defineProps({
  onCreated: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['modal-close'])
const toast = useToast()

const url = ref('')
const file = ref(null)
const submitting = ref(false)
const error = ref('')
const dragOver = ref(false)
const skipVerification = ref(false)

const fileInput = ref(null)

function clearUrl() {
  url.value = ''
  error.value = ''
}

function clearFile() {
  file.value = null
  error.value = ''
  if (fileInput.value) fileInput.value.value = ''
}

function onFileSelect(e) {
  const f = e.target.files?.[0]
  if (f) validateAndSetFile(f)
}

function onDrop(e) {
  dragOver.value = false
  const f = e.dataTransfer?.files?.[0]
  if (f) validateAndSetFile(f)
}

function validateAndSetFile(f) {
  error.value = ''
  if (!f.name.toLowerCase().endsWith('.pdf') && f.type !== 'application/pdf') {
    error.value = 'Only PDF files are accepted'
    return
  }
  if (f.size > 30 * 1024 * 1024) {
    error.value = 'File too large (max 30MB)'
    return
  }
  file.value = f
  url.value = ''
}

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function isValidUrl(str) {
  try {
    const u = new URL(str)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

async function submit() {
  error.value = ''

  if (!url.value && !file.value) {
    error.value = 'Provide a URL or select a PDF file'
    return
  }

  if (url.value && !isValidUrl(url.value)) {
    error.value = 'Please enter a valid URL (http:// or https://)'
    return
  }

  submitting.value = true

  try {
    let result
    if (file.value) {
      result = await Signal.createFromPDF(file.value)
    } else {
      result = await Signal.createFromURL(url.value, skipVerification.value)
    }

    if (result.status === 'success') {
      toast.success('Signal created — processing started')
      props.onCreated(result.data)
      emit('modal-close')
    } else {
      error.value = result.message || 'Failed to create signal'
    }
  } catch (e) {
    error.value = 'An unexpected error occurred'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="create-signal">
    <h3 class="modal-title">Create Signal</h3>
    <p class="modal-description">
      Submit a URL or upload a PDF report for analysis.
    </p>

    <!-- Error -->
    <div v-if="error" class="error-msg">
      <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
      {{ error }}
    </div>

    <!-- URL input -->
    <div v-if="!file" class="input-section">
      <label class="input-label">URL</label>
      <div class="url-input-wrap">
        <input
          v-model="url"
          type="text"
          placeholder="https://example.com/threat-report"
          :disabled="submitting"
          @keydown.enter="submit"
        />
        <button v-if="url" class="btn-clear" @click="clearUrl" :disabled="submitting">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>
    </div>

    <!-- Skip verification toggle (URL mode only) -->
    <div v-if="!file && url" class="skip-verify-row">
      <label class="toggle-label">
        <input type="checkbox" v-model="skipVerification" :disabled="submitting" />
        <span class="toggle-text">Skip relevance check</span>
      </label>
      <span class="toggle-hint">Bypass the automated relevance filter</span>
    </div>

    <!-- File selected display -->
    <div v-if="file" class="file-selected">
      <div class="file-info">
        <font-awesome-icon :icon="['fas', 'file-pdf']" class="file-icon" />
        <div class="file-meta">
          <span class="file-name">{{ file.name }}</span>
          <span class="file-size">{{ formatSize(file.size) }}</span>
        </div>
      </div>
      <button class="btn-clear" @click="clearFile" :disabled="submitting">
        <font-awesome-icon :icon="['fas', 'times']" />
      </button>
    </div>

    <!-- Divider -->
    <div v-if="!file && !url" class="divider-row">
      <div class="divider-line"></div>
      <span class="divider-text">or</span>
      <div class="divider-line"></div>
    </div>

    <!-- PDF drop zone -->
    <div
      v-if="!file && !url"
      class="drop-zone"
      :class="{ 'drag-over': dragOver }"
      @dragenter.prevent="dragOver = true"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop"
      @click="fileInput?.click()"
    >
      <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" class="drop-icon" />
      <span class="drop-text">Drop a PDF here or <span class="drop-link">browse</span></span>
      <span class="drop-hint">Max 30MB</span>
    </div>

    <input
      ref="fileInput"
      type="file"
      accept=".pdf,application/pdf"
      style="display: none"
      @change="onFileSelect"
    />

    <!-- Actions -->
    <div class="modal-actions">
      <button
        class="btn-submit"
        :disabled="submitting || (!url && !file)"
        @click="submit"
      >
        <font-awesome-icon v-if="submitting" :icon="['fas', 'spinner']" spin />
        <font-awesome-icon v-else :icon="['fas', 'bolt']" />
        {{ submitting ? 'Submitting...' : 'Analyze' }}
      </button>
      <button class="btn-cancel" @click="emit('modal-close')" :disabled="submitting">
        Cancel
      </button>
    </div>
  </div>
</template>

<style scoped>
.create-signal {
  width: 480px;
  max-width: 90vw;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-heading);
  margin: 0 0 0.5rem 0;
}

.modal-description {
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 0 0 1.25rem 0;
}

.error-msg {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: var(--color-error-soft);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: var(--radius-sm);
  color: var(--color-error-text);
  font-size: 0.8125rem;
  margin-bottom: 1rem;
}

.input-section {
  margin-bottom: 0;
}

.input-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-faint);
  margin-bottom: 0.375rem;
}

.url-input-wrap {
  display: flex;
  align-items: center;
  background: var(--surface-overlay);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  overflow: hidden;
  transition: border-color 0.15s ease;
}

.url-input-wrap:focus-within {
  border-color: var(--accent);
}

.url-input-wrap input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 0.625rem 0.75rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: inherit;
}

.url-input-wrap input::placeholder {
  color: var(--text-disabled);
}

.btn-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: transparent;
  border: none;
  color: var(--text-faint);
  cursor: pointer;
  flex-shrink: 0;
  margin-right: 0.25rem;
  border-radius: var(--radius-xs);
  transition: var(--transition-fast);
}

.btn-clear:hover:not(:disabled) {
  color: var(--text-muted);
  background: var(--surface-overlay-2);
}

.file-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: var(--accent-soft);
  border: 1px solid rgba(115, 87, 210, 0.2);
  border-radius: var(--radius-sm);
  margin-bottom: 0.75rem;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.file-icon {
  font-size: 1.5rem;
  color: var(--color-error);
  flex-shrink: 0;
}

.file-meta {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.file-name {
  font-size: 0.8125rem;
  color: var(--text-primary);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: 0.6875rem;
  color: var(--text-faint);
}

.skip-verify-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.75rem;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.toggle-label input[type="checkbox"] {
  width: 0.875rem;
  height: 0.875rem;
  accent-color: var(--accent);
  cursor: pointer;
}

.toggle-text {
  font-size: 0.8125rem;
  color: var(--text-primary);
  font-weight: 500;
}

.toggle-hint {
  font-size: 0.6875rem;
  color: var(--text-disabled);
}

.divider-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1rem 0;
}

.divider-line {
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
}

.divider-text {
  font-size: 0.6875rem;
  color: var(--text-disabled);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem;
  border: 2px dashed rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition);
}

.drop-zone:hover,
.drop-zone.drag-over {
  border-color: rgba(115, 87, 210, 0.4);
  background: rgba(115, 87, 210, 0.05);
}

.drop-icon {
  font-size: 1.5rem;
  color: var(--text-disabled);
}

.drop-zone:hover .drop-icon,
.drop-zone.drag-over .drop-icon {
  color: var(--accent);
}

.drop-text {
  font-size: 0.8125rem;
  color: var(--text-faint);
}

.drop-link {
  color: var(--accent);
  font-weight: 500;
}

.drop-hint {
  font-size: 0.6875rem;
  color: var(--text-disabled);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.btn-submit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(115, 87, 210, 0.2);
  border: 1px solid rgba(115, 87, 210, 0.4);
  border-radius: var(--radius-sm);
  color: var(--accent-text);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

.btn-submit:hover:not(:disabled) {
  background: rgba(115, 87, 210, 0.3);
  border-color: rgba(115, 87, 210, 0.6);
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  background: var(--surface-overlay-2);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

.btn-cancel:hover:not(:disabled) {
  background: var(--surface-overlay-3);
  color: var(--text-primary);
}
</style>
